import axios from "axios";
import { apiEndpoint, apiEndpointFunction } from "@iso/lib/helpers/endpoint";
import { authHeader } from "@iso/lib/helpers/authHeader";

export const mapViewService = {
  getList,
  create,
  update,
  destroy,
  setUserDefault,
  setPropertyDefault,
  deletePropertyDefault,
};

/**
 * Handle API response — return data on success, throw on error.
 */
function handleResponse(res) {
  if (res && res.status === 200 && res.data.code === "0000") {
    return res.data;
  }
  const errorMsg = res?.data?.message || 'Request failed';
  throw new Error(errorMsg);
}

/**
 * Get list of map views for a property
 * @param {Object} params - { property_id }
 */
function getList(params = {}) {
  return axios
    .get(apiEndpoint.mapViews, {
      params: params,
      headers: authHeader(),
    })
    .then(handleResponse);
}

/**
 * Create a new map view
 * @param {Object} data - { property_id, name, config }
 */
function create(data) {
  return axios
    .post(apiEndpoint.mapViewsCreate, data, {
      headers: authHeader(),
    })
    .then(handleResponse);
}

/**
 * Update an existing map view
 * @param {Number} id - View ID
 * @param {Object} data - { name, config }
 */
function update(id, data) {
  return axios
    .post(apiEndpointFunction.mapViewsUpdate(id), data, {
      headers: authHeader(),
    })
    .then(handleResponse);
}

/**
 * Delete a map view
 * @param {Number} id - View ID
 */
function destroy(id) {
  return axios
    .post(
      apiEndpointFunction.mapViewsDelete(id),
      {},
      {
        headers: authHeader(),
      }
    )
    .then(handleResponse);
}

/**
 * Set a view as the user's default
 * @param {Number} id - View ID (0 to clear)
 */
function setUserDefault(id) {
  return axios
    .post(
      apiEndpointFunction.mapViewsSetUserDefault(id),
      {},
      {
        headers: authHeader(),
      }
    )
    .then(handleResponse);
}

/**
 * Set a view as the property default (owner/admin only).
 * Clones the source view into a standalone property default record.
 * @param {Number} id - Source view ID to clone
 */
function setPropertyDefault(id) {
  return axios
    .post(
      apiEndpointFunction.mapViewsSetPropertyDefault(id),
      {},
      {
        headers: authHeader(),
      }
    )
    .then(handleResponse);
}

/**
 * Delete the property default view (owner/admin only)
 * @param {Number} propertyId - Property ID
 */
function deletePropertyDefault(propertyId) {
  return axios
    .post(
      apiEndpointFunction.mapViewsDeletePropertyDefault(propertyId),
      {},
      {
        headers: authHeader(),
      }
    )
    .then(handleResponse);
}
